import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Star, Quote, PenLine, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEED = [
  { name: "Priya Deshmukh", treatment: "Root Canal Treatment", rating: 5, text: "I was terrified of my root canal, but Dr. Ruchika made it completely painless. The clinic is spotless and the staff are so kind. Highly recommend!" },
  { name: "Amit Kulkarni", treatment: "Dental Implants", rating: 5, text: "Got two implants done here. The results look and feel completely natural. Dr. Sontakay explained every step patiently. Best dental care in Nagpur." },
  { name: "Sneha Raut", treatment: "Teeth Whitening", rating: 5, text: "My smile has never looked better! The whitening was quick and comfortable, and the team made me feel at ease throughout. Thank you, Kalpataru!" },
  { name: "Rahul Meshram", treatment: "Clear Aligners", rating: 5, text: "Six months into my aligners and the difference is amazing. Dr. Ruchika's personalized approach and honest advice made all the difference." },
];

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", treatment: "", text: "" });
  const [rating, setRating] = useState(5);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews`);
      setReviews(data || []);
    } catch (e) {
      setReviews([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const display = reviews.length ? reviews : SEED;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) {
      toast.error("Please add your name and review.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, { ...form, rating });
      toast.success("Thank you! Your review has been submitted for approval.");
      setForm({ name: "", treatment: "", text: "" });
      setRating(5);
      setShowForm(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reviews" className="brand-gradient grain-texture py-20 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-gold-light font-700 text-sm uppercase tracking-wider">
            Happy Patients
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mt-2">
            Smiles We've Created
          </h2>
          <div className="flex items-center justify-center gap-1 mt-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={20} className="text-gold-light fill-current" />
            ))}
            <span className="text-sage-100 text-sm font-600 ml-2">Rated 5.0 by our patients</span>
          </div>
          <Button
            data-testid="review-open-form-btn"
            onClick={() => setShowForm(!showForm)}
            className="rounded-full bg-gold hover:bg-gold-dark text-sage-900 font-700 mt-6"
          >
            {showForm ? <><X size={16} className="mr-1" /> Close</> : <><PenLine size={16} className="mr-1" /> Share Your Experience</>}
          </Button>
        </div>

        {showForm && (
          <motion.form
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={submit}
            data-testid="review-form"
            className="bg-white rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto mt-8 space-y-4 shadow-xl"
          >
            <h3 className="font-heading text-2xl text-sage-900">Tell us about your visit</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="rname">Your Name *</Label>
                <Input id="rname" data-testid="review-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rtreat">Treatment (optional)</Label>
                <Input id="rtreat" data-testid="review-treatment-input" value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} placeholder="e.g. Teeth Whitening" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Your Rating</Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`review-rating-${n}`} onClick={() => setRating(n)} className="p-1">
                    <Star size={26} className={n <= rating ? "text-gold fill-current" : "text-sage-300"} />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rtext">Your Review *</Label>
              <Textarea id="rtext" data-testid="review-text-input" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={4} placeholder="Share your experience with us..." />
            </div>
            <Button type="submit" data-testid="review-submit-btn" disabled={loading} className="rounded-full bg-sage-700 hover:bg-sage-800 text-white font-700">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Submit Review"}
            </Button>
            <p className="text-xs text-sage-400">Reviews appear on the site after approval by our team.</p>
          </motion.form>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {display.map((r, i) => (
            <motion.div
              key={`${r.name}-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
              data-testid={`review-card-${i}`}
              className="bg-white rounded-2xl p-6 shadow-sm flex flex-col"
            >
              <Quote className="text-gold mb-3" size={28} />
              <p className="text-sage-700 text-sm leading-relaxed flex-1">"{r.text}"</p>
              <div className="flex gap-0.5 mt-4">
                {[...Array(r.rating || 5)].map((_, s) => (
                  <Star key={s} size={14} className="text-gold fill-current" />
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-sage-100">
                <div className="font-700 text-sage-900">{r.name}</div>
                {r.treatment && <div className="text-xs text-sage-500">{r.treatment}</div>}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
